import { Badge } from '@fluentui/react-components';
import { DismissFilled } from '@fluentui/react-icons';
import cn from 'classnames';
import React, { HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import './SelectionCountBadge.scss';

export interface SelectionCountBadgeProps
  extends Omit<HTMLAttributes<HTMLElement>, 'children' | 'color'> {
  /** Number of currently selected entries */
  count: number;
  /** Total number of selectable entries */
  total: number;
  /** Called when the badge is activated (click or keyboard) to clear the selection */
  onReset?: () => void;
  /** Additional CSS class */
  className?: string;
}

/**
 * SelectionCountBadge - unified display of the number of selected entries in
 * list/table toolbars, rendered as "{{count}} von {{total}} ausgewählt".
 *
 * Renders nothing while nothing is selected. When `onReset` is provided the
 * badge shows a dismiss icon and can be clicked (or activated via keyboard) to
 * clear the current selection.
 *
 * @example
 * <SelectionCountBadge
 *   count={selected.length}
 *   total={items.length}
 *   onReset={handleDeselectAll}
 * />
 */
export const SelectionCountBadge = ({
  count,
  total,
  onReset,
  className,
  ...rest
}: SelectionCountBadgeProps) => {
  const { t } = useTranslation();

  if (count <= 0) {
    return null;
  }

  const label = t('selection_count_of', {
    defaultValue: '{{count}} von {{total}} ausgewählt',
    count,
    total,
  });

  const interactive = Boolean(onReset);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (!onReset) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onReset();
    }
  };

  const resetLabel = t('reset_selection', { defaultValue: 'Auswahl zurücksetzen' });

  return (
    <Badge
      {...rest}
      className={cn('selectionCountBadge', className)}
      appearance="tint"
      color="informative"
      icon={interactive ? <DismissFilled /> : undefined}
      iconPosition="after"
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? `${label} – ${resetLabel}` : undefined}
      onClick={onReset}
      onKeyDown={handleKeyDown}
    >
      {label}
    </Badge>
  );
};

SelectionCountBadge.displayName = 'SelectionCountBadge';
